/**
 * Доля ядра публичного рендерера: вид `payment_banners` (значки способов оплаты).
 *
 * WEB-2921, заход 2: перенесено из `PublicBlockRenderer.tsx` слово в слово.
 * Метка `case 'payment_banners'` остаётся в ядре; тело ветки — функция отрисовки
 * `renderPaymentBanners(ctx)`, замыкания ядра приходят объектом-контекстом
 * (`CoreRenderContext`). Хуков здесь нет. Доля едет в первом экране вместе с
 * ядром (статический импорт), ленивого куска у неё нет.
 *
 * Значки рисует `TrustBadgeIcon` (trustBadgeIcons.tsx) — та же таблица, что у
 * плашек доверия; какие способы показывать, решает автор в панели. Снятые
 * значки (PayPal, WEB-2960) покупателю не рисуются даже из старых данных.
 */
import React from 'react';
import { TrustBadgeIcon } from '../trustBadgeIcons';
import type { CoreRenderContext } from '../PublicBlockRenderer';

export function renderPaymentBanners({
  block,
}: CoreRenderContext): React.ReactElement | null {
  const labels: Record<string, string> = {
    visa: 'VISA',
    mastercard: 'MasterCard',
    apple_pay: 'Apple Pay', // i18n-allow: название платёжного бренда, не переводится
    google_pay: 'Google Pay', // i18n-allow: название платёжного бренда, не переводится
  };
  // WEB-2960 (WEB-2314, вариант А): значок PayPal снят — оплата принимает
  // только карту (WEB-1449), PayPal на странице оплаты нет. У собранных
  // воронок он лежит в данных — отсеиваем при отрисовке. Список снятых —
  // `paymentBannerOffer.ts` (сюда не импортируется ради веса первого экрана;
  // совпадение держит тест доли, перебирающий RETIRED_PAYMENT_BANNERS).
  const banners = (block.banners ?? []).filter((b) => b !== 'paypal');
  if (banners.length === 0) return null;
  // QA WEB-673: вид «SVG-иконки» вместо текст-плашек (по выбору в панели).
  if (block.bannersStyle === 'icons') {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 14,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        {banners.map((b) => (
          <span
            key={b}
            title={labels[b] ?? b}
            style={{ display: 'inline-flex', opacity: 0.9 }}
          >
            <TrustBadgeIcon badge={b} />
          </span>
        ))}
      </div>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 8,
        marginBottom: 16,
        flexWrap: 'wrap',
      }}
    >
      {banners.map((b) => (
        <span
          key={b}
          style={{
            padding: '6px 12px',
            // WEB-925: security-ряд платёжек — денежный акцент пака.
            // Без пака переменной нет → фолбэк = сегодняшняя рамка.
            border:
              '1px solid var(--accent-money, var(--color-border-primary, #d9d9d9))',
            borderRadius: 'var(--border-radius-sm, 6px)',
            fontSize: 12,
            fontWeight: 600,
            // WEB-1008: пилюля лежит на светлой поверхности → текст обязан
            // читать on-surface (класс WEB-966), иначе тёмная палитра даёт
            // белое на белом.
            color:
              'var(--color-text-on-surface, var(--color-text-secondary, #595959))',
            background: 'var(--color-bg-surface, #fff)',
          }}
        >
          {labels[b] ?? b}
        </span>
      ))}
    </div>
  );
}
